/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped Supabase joins; replace with generated types (tech debt) */
import { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { CreditCard, ChevronDown, ChevronUp, Plus, Pencil } from 'lucide-react';
import { SortingState } from '@tanstack/react-table';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import type { CredentialRequest } from '@/models/credentialRequest';
import { CREDENTIAL_TYPES } from '@/models/credentialRequest';
import { Button } from '@/components/ui/button';
import { RequestsTable } from '@/features/credential-requests/components/organisms/RequestsTable';
import { RequestFilters } from '@/features/credential-requests/components/organisms/RequestFilters';
import { useCredentialRequestList } from '@/features/credential-requests/hooks/useCredentialRequestList';
import { useRequestListActions } from '@/features/credential-requests/hooks/useRequestListActions';
import { useUserProfile } from '@/features/credential-requests/hooks/useUserProfile';
import { usePermissions } from '@/hooks/usePermissions';
import { RejectRequestDialog } from '@/features/credential-requests/components/organisms/RejectRequestDialog';
import { DataTableView } from '@/components/organisms/DataTableView';
import { ColumnDef } from '@tanstack/react-table';
import { type MinisterAvailability } from '@/Services/ministers.service';
import { Checkbox } from '@/components/ui/checkbox';
import { CreateRequestConfirmDialog } from './CreateRequestConfirmDialog';
import { getNextAvailableCodeNumber } from '@/Services/credentials.service';
import { supabase } from '@/Services/supabaseClient';
import { useToast } from '@/hooks/use-toast';

interface TerritoryContext {
  district: District | null;
  region: Region | null;
  isDistrict: boolean;
}

export default function CredentialsTab() {
  const { district, region, isDistrict } = useOutletContext<TerritoryContext>();
  const navigate = useNavigate();
  const { user } = useUserProfile();
  const { can } = usePermissions();
  const canEditMinister = can('minister.edit');
  const { toast } = useToast();

  // Section collapse states
  const [requestsExpanded, setRequestsExpanded] = useState(true);
  const [ministersExpanded, setMinistersExpanded] = useState(true);

  // Reject dialog state
  const [rejectingRequest, setRejectingRequest] = useState<CredentialRequest | null>(null);
  
  // Create request confirmation dialog state
  const [showCreateConfirmDialog, setShowCreateConfirmDialog] = useState(false);
  const [nextCode, setNextCode] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  // Requests filters
  const [statusFilter, setStatusFilter] = useState("");
  const [regionFilterInDistrict, setRegionFilterInDistrict] = useState(""); // For filtering by region in district scope
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [requestsSearchValue, setRequestsSearchValue] = useState("");
  const [requestsSorting, setRequestsSorting] = useState<SortingState>([]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPageSize, setRequestsPageSize] = useState(20);

  // Load district regions for filtering in district scope
  const [districtRegions, setDistrictRegions] = useState<Region[]>([]);
  useEffect(() => {
    const loadDistrictRegions = async () => {
      if (isDistrict && district) {
        const { supabase } = await import('@/Services/supabaseClient');
        const { data, error } = await supabase
          .from('regions')
          .select('id, name, code')
          .eq('district_id', district.id)
          .eq('is_active', true)
          .order('name');
        
        if (!error && data) {
          setDistrictRegions(data as any);
        }
      }
    };
    loadDistrictRegions();
  }, [isDistrict, district]);

  // Available ministers state
  const [availableMinisters, setAvailableMinisters] = useState<MinisterAvailability[]>([]);
  const [ministersLoading, setMinistersLoading] = useState(false);
  const [ministersSearchValue, setMinistersSearchValue] = useState("");
  const [ministersSorting, setMinistersSorting] = useState<SortingState>([]);
  const [ministersPage, setMinistersPage] = useState(1);
  const [ministersPageSize, setMinistersPageSize] = useState(20);
  const [selectedMinisters, setSelectedMinisters] = useState<Set<string>>(new Set());

  // Scoped filters based on territory
  const scopedDistrictFilter = isDistrict ? district?.id || "" : region?.district_id || "";
  const scopedRegionFilter = !isDistrict && region ? region.id : regionFilterInDistrict;

  // Load credential requests with scoped filters
  const {
    requests,
    isLoading: requestsLoading,
    totalPages: requestsTotalPages,
    totalItems: requestsTotalItems,
    reloadRequests,
  } = useCredentialRequestList({
    statusFilter,
    districtFilter: scopedDistrictFilter,
    regionFilter: scopedRegionFilter,
    dateFromFilter,
    dateToFilter,
    searchValue: requestsSearchValue,
    sorting: requestsSorting,
    currentPage: requestsPage,
    pageSize: requestsPageSize,
    credentialType: CREDENTIAL_TYPES.MINISTERIAL, // ✅ Only show MINISTERIAL requests
  });

  // Load available ministers
  useEffect(() => {
    const loadAvailableMinisters = async () => {
      if (!region && !district) return;

      setMinistersLoading(true);
      try {
        const currentYear = new Date().getFullYear();
        const { getMinistersWithAvailability } = await import('@/Services/ministers.service');
        
        // Use the new optimized function for both district and region scopes
        const ministersWithAvailability = await getMinistersWithAvailability({
          districtId: isDistrict ? district?.id : undefined,
          regionId: region?.id,
          year: currentYear,
          credentialType: CREDENTIAL_TYPES.MINISTERIAL,
        });

        // Filter to only show AVAILABLE ministers
        const available = ministersWithAvailability.filter(
          (m) => m.availability_status === 'AVAILABLE'
        );
        setAvailableMinisters(available);
      } catch (err) {
        console.error("Error loading available ministers:", err);
        setAvailableMinisters([]);
      } finally {
        setMinistersLoading(false);
      }
    };

    loadAvailableMinisters();
  }, [district, region, isDistrict]);

  // Request actions
  const {
    isSubmitting,
    handleApprove: approveRequest,
    handleReject: rejectRequest,
    handleMarkPrinted: markPrintedRequest,
    handleMarkDelivered: markDeliveredRequest,
  } = useRequestListActions({
    userId: user?.id,
    onSuccess: reloadRequests,
  });

  // Handle row click to navigate to detail
  const handleRowClick = (request: CredentialRequest) => {
    navigate(`/credential-requests/${request.id}`);
  };

  // Handle reject dialog
  const handleRejectOpen = (request: CredentialRequest) => {
    setRejectingRequest(request);
  };

  const handleRejectClose = () => {
    setRejectingRequest(null);
  };

  const handleRejectConfirm = async (notes: string) => {
    if (rejectingRequest) {
      await rejectRequest(rejectingRequest.id, notes);
      setRejectingRequest(null);
    }
  };

  // Wrap action handlers to convert from request object to request ID
  const handleApprove = (request: CredentialRequest) => {
    approveRequest(request.id);
  };

  const handleMarkPrinted = (request: CredentialRequest) => {
    markPrintedRequest(request.id);
  };

  const handleMarkDelivered = (request: CredentialRequest) => {
    markDeliveredRequest(request.id);
  };

  // Handle create new request
  const handleCreateNew = async () => {
    if (selectedMinisters.size === 0) return;
    
    try {
      // Get next available code
      const currentYear = new Date().getFullYear();
      const nextNumber = await getNextAvailableCodeNumber(currentYear);
      const yearSuffix = currentYear.toString().slice(-2);
      const paddedNumber = String(nextNumber).padStart(3, '0');
      const code = `SOL${yearSuffix}-${paddedNumber}`;
      
      setNextCode(code);
      setShowCreateConfirmDialog(true);
    } catch (error) {
      console.error('Error getting next code:', error);
      toast({
        title: 'Error',
        description: 'No se pudo obtener el código de solicitud',
        variant: 'destructive',
      });
    }
  };
  
  // Handle create request confirmation
  const handleCreateConfirm = async () => {
    if (selectedMinisters.size === 0 || !user?.id) return;
    
    setIsCreating(true);
    try {
      const currentYear = new Date().getFullYear();
      const territoryDistrictId = isDistrict ? district?.id : region?.district_id;
      const territoryRegionId = region?.id;
      
      if (!territoryDistrictId || !territoryRegionId) {
        throw new Error('Territorio no válido');
      }
      
      // Create credential request with SUBMITTED status
      type CredentialRequestInsert = {
        district_id: string;
        region_id: string;
        year: number;
        status: string;
        created_by: string;
        created_at: string;
        submitted_at: string;
      };
      
      const { data: newRequest, error: requestError } = await supabase
        .from('credential_requests')
        .insert({
          district_id: territoryDistrictId,
          region_id: territoryRegionId,
          year: currentYear,
          status: 'SUBMITTED', // Auto-generates code via trigger
          created_by: user.id,
          created_at: new Date().toISOString(),
          submitted_at: new Date().toISOString(),
        } as CredentialRequestInsert as any)
        .select()
        .single() as { data: any; error: any };
      
      if (requestError) throw requestError;
      
      // Add selected ministers to the request
      const ministerRequestInserts = Array.from(selectedMinisters).map(ministerId => ({
        credential_request_id: newRequest.id,
        minister_id: ministerId,
      }));
      
      const { error: ministersError } = await supabase
        .from('credential_request_ministers')
        .insert(ministerRequestInserts as any) as { error: any };
      
      if (ministersError) throw ministersError;
      
      // Success!
      toast({
        title: 'Solicitud creada y enviada',
        description: `Se creó la solicitud ${newRequest.code} con ${selectedMinisters.size} ministro${selectedMinisters.size !== 1 ? 's' : ''}`,
      });
      
      // Clear selection and refresh data
      setSelectedMinisters(new Set());
      setShowCreateConfirmDialog(false);
      reloadRequests();
      
      // Reload ministers to reflect the new "taken" status
      const loadAvailableMinisters = async () => {
        if (!region && !district) return;

        setMinistersLoading(true);
        try {
          const currentYear = new Date().getFullYear();
          const { getMinistersWithAvailability } = await import('@/Services/ministers.service');
          
          const ministersWithAvailability = await getMinistersWithAvailability({
            districtId: isDistrict ? district?.id : undefined,
            regionId: region?.id,
            year: currentYear,
            credentialType: CREDENTIAL_TYPES.MINISTERIAL,
          });

          const available = ministersWithAvailability.filter(
            (m) => m.availability_status === 'AVAILABLE'
          );
          setAvailableMinisters(available);
        } catch (err) {
          console.error("Error loading available ministers:", err);
        } finally {
          setMinistersLoading(false);
        }
      };
      
      await loadAvailableMinisters();
      
    } catch (error) {
      console.error('Error creating request:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'No se pudo crear la solicitud',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Ministers table columns
  const ministersColumns: ColumnDef<MinisterAvailability>[] = useMemo(
    () => [
      // Only show checkbox column in region scope (not district scope)
      ...(!isDistrict ? [{
        id: "select",
        header: ({ table }: { table: any }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }: { row: any }) => (
          <Checkbox
            checked={selectedMinisters.has(row.original.id)}
            onCheckedChange={(checked) => {
              const newSelected = new Set(selectedMinisters);
              if (checked) {
                newSelected.add(row.original.id);
              } else {
                newSelected.delete(row.original.id);
              }
              setSelectedMinisters(newSelected);
            }}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }] : []),
      {
        accessorKey: "adb_code",
        header: "Código",
      },
      {
        accessorKey: "full_name",
        header: "Nombre Completo",
      },
      {
        accessorKey: "dni",
        header: "DNI",
      },
      {
        accessorKey: "degree",
        header: "Grado",
        cell: ({ row }) => row.original.degree?.name || "—",
      },
      ...(isDistrict
        ? [
            {
              accessorKey: "region",
              header: "Región",
              cell: ({ row }: { row: { original: MinisterAvailability } }) =>
                row.original.region?.name || "—",
            },
          ]
        : []),
      ...(canEditMinister ? [{
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }: { row: { original: MinisterAvailability } }) => (
          <Button
            variant="ghost"
            size="sm"
            className="size-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/ministers/${row.original.id}`);
            }}
          >
            <Pencil className="size-4" />
          </Button>
        ),
      }] : []),
    ],
    [isDistrict, canEditMinister, selectedMinisters, navigate]
  );

  // Filter ministers by search
  const filteredMinisters = useMemo(() => {
    if (!ministersSearchValue) return availableMinisters;

    // Split search into tokens (words) for flexible matching
    const searchTokens = ministersSearchValue.toLowerCase().trim().split(/\s+/).filter(token => token.length > 0);
    
    return availableMinisters.filter((m) => {
      // Normalize ADB code by removing dashes for flexible matching (e.g., "12364" matches "12-364")
      const normalizedCode = (m.adb_code || "").replace(/-/g, "");
      const searchableText = `${m.full_name || ""} ${m.adb_code || ""} ${normalizedCode} ${m.dni || ""}`.toLowerCase();
      
      // Check if ALL search tokens exist in the searchable text (AND logic)
      return searchTokens.every(token => searchableText.includes(token));
    });
  }, [availableMinisters, ministersSearchValue]);

  // Paginate filtered ministers
  const paginatedMinisters = useMemo(() => {
    const startIndex = (ministersPage - 1) * ministersPageSize;
    const endIndex = startIndex + ministersPageSize;
    return filteredMinisters.slice(startIndex, endIndex);
  }, [filteredMinisters, ministersPage, ministersPageSize]);

  const ministersTotalPages = Math.ceil(filteredMinisters.length / ministersPageSize);

  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        {/* Requests Section */}
        <div className={`flex flex-col rounded-lg border bg-card transition-all ${requestsExpanded ? 'max-h-[70vh]' : 'max-h-[60px]'}`}>
          <div className="flex shrink-0 items-center justify-between border-b bg-card px-6 py-3">
            <div className="flex items-center gap-3">
              <CreditCard className="size-5 text-primary" />
              <h2 className="text-lg font-semibold">Solicitudes de Credenciales</h2>
              <span className="text-sm text-muted-foreground">
                ({requestsTotalItems} solicitudes)
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRequestsExpanded(!requestsExpanded)}
            >
              {requestsExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </Button>
          </div>

          {requestsExpanded && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
              <div className="mb-4 shrink-0">
                <RequestFilters
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                  districtFilter={scopedDistrictFilter}
                  onDistrictChange={() => {}} // Disabled in scoped view
                  regionFilter={scopedRegionFilter}
                  onRegionChange={setRegionFilterInDistrict} // Allow region filtering in district scope
                  dateFromFilter={dateFromFilter}
                  onDateFromChange={setDateFromFilter}
                  dateToFilter={dateToFilter}
                  onDateToChange={setDateToFilter}
                  districts={district ? [district] : []}
                  regions={isDistrict ? districtRegions : (region ? [region] : [])}
                  isScoped={true}
                  showRegionOnly={isDistrict} // Show region filter in district scope
                />
              </div>

              <div className="flex-1 overflow-auto">
                <RequestsTable
                  requests={requests}
                  isLoading={requestsLoading}
                            isSubmitting={isSubmitting}
                  currentPage={requestsPage}
                  totalPages={requestsTotalPages}
                  totalItems={requestsTotalItems}
                  pageSize={requestsPageSize}
                  pageSizeOptions={[10, 20, 50]}
                  onPageChange={setRequestsPage}
                  onPageSizeChange={setRequestsPageSize}
                  searchValue={requestsSearchValue}
                  onSearchChange={setRequestsSearchValue}
                  sorting={requestsSorting}
                  onSortingChange={setRequestsSorting}
                  onRowClick={handleRowClick}
                  onApprove={handleApprove}
                  onRejectClick={handleRejectOpen}
                  onMarkPrinted={handleMarkPrinted}
                  onMarkDelivered={handleMarkDelivered}
                  onCreateNew={() => {
                    const currentYear = new Date().getFullYear();
                    const params = new URLSearchParams({
                      year: currentYear.toString(),
                    });
                    
                    if (isDistrict && district) {
                      params.set('districtId', district.id);
                    }
                    
                    if (region) {
                      params.set('regionId', region.id);
                      if (region.district_id) {
                        params.set('districtId', region.district_id);
                      }
                    }
                    
                    navigate(`/credential-requests/new?${params.toString()}`);
                  }}
                  columnOptions={{
                    hideDistrict: true, // Always hide district in scoped view
                    hideRegion: !isDistrict, // Only hide region in region scope, show in district scope
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Available Ministers Section */}
        <div className={`flex flex-col rounded-lg border bg-card transition-all ${ministersExpanded ? 'max-h-[70vh]' : 'max-h-[60px]'}`}>
          <div className="flex shrink-0 items-center justify-between border-b bg-card px-6 py-3">
            <div className="flex items-center gap-3">
              <CreditCard className="size-5 text-primary" />
              <h2 className="text-lg font-semibold">Ministros Sin Credencial</h2>
              <span className="text-sm text-muted-foreground">
                ({filteredMinisters.length} disponibles)
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Only show Create button in region scope, not district scope */}
              {!isDistrict && (
                <Button
                  onClick={handleCreateNew}
                  disabled={selectedMinisters.size === 0}
                  size="sm"
                >
                  <Plus className="mr-2 size-4" />
                  Crear y Enviar
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMinistersExpanded(!ministersExpanded)}
              >
                {ministersExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
              </Button>
            </div>
          </div>

          {ministersExpanded && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
              <div className="flex-1 overflow-auto">
                <DataTableView
                  columns={ministersColumns}
                  data={paginatedMinisters}
                  isLoading={ministersLoading}
                  sorting={ministersSorting}
                  onSortingChange={setMinistersSorting}
                  currentPage={ministersPage}
                  totalPages={ministersTotalPages}
                  totalItems={filteredMinisters.length}
                  pageSize={ministersPageSize}
                  pageSizeOptions={[10, 20, 50]}
                  onPageChange={setMinistersPage}
                  onPageSizeChange={setMinistersPageSize}
                  searchValue={ministersSearchValue}
                  onSearchChange={setMinistersSearchValue}
                  emptyMessage="No hay ministros disponibles"
                  onRowClick={!isDistrict ? (minister) => {
                    // Toggle selection on row click (only in region scope)
                    const newSelected = new Set(selectedMinisters);
                    if (newSelected.has(minister.id)) {
                      newSelected.delete(minister.id);
                    } else {
                      newSelected.add(minister.id);
                    }
                    setSelectedMinisters(newSelected);
                  } : undefined}
                  getRowClassName={!isDistrict ? (minister) => {
                    // Highlight selected rows (only in region scope)
                    return selectedMinisters.has(minister.id) ? 'bg-primary/20 hover:bg-primary/30' : '';
                  } : undefined}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      {rejectingRequest && (
        <RejectRequestDialog
          open={!!rejectingRequest}
          onOpenChange={(open) => !open && handleRejectClose()}
          onConfirm={handleRejectConfirm}
          isSubmitting={isSubmitting}
        />
      )}
      
      {/* Create Request Confirmation Dialog */}
      <CreateRequestConfirmDialog
        open={showCreateConfirmDialog}
        onOpenChange={setShowCreateConfirmDialog}
        code={nextCode}
        ministerCount={selectedMinisters.size}
        isSubmitting={isCreating}
        onConfirm={handleCreateConfirm}
      />
    </div>
  );
}
